import React, { useEffect, useRef, useState } from 'react';
import { Card, Typography } from "@material-tailwind/react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEye,
    faCheckCircle,
    faTimesCircle,
    faEdit,
    faTrash,
    faEllipsisVertical,
    faClock,
    faCircleCheck,
    faCircleXmark,
    faUndo
} from '@fortawesome/free-solid-svg-icons';
import { formatCurrency, formatDate } from '../utils';

const StatusIcon = ({ status, type }) => {
    const normalized = (status || '').toUpperCase();
    if (type === 'reimbursement' && !status) return null;

    let icon = faCircleCheck;
    let color = 'text-gray-400';
    let title = normalized || 'N/A';

    if (normalized === 'APPROVED') {
        icon = faCircleCheck;
        color = 'text-green-600';
        title = 'Approved';
    } else if (normalized === 'PENDING') {
        icon = faClock;
        color = 'text-amber-600';
        title = type === 'reimbursement' ? 'Reimbursement pending' : 'Pending approval';
    } else if (normalized === 'REJECTED') {
        icon = faCircleXmark;
        color = 'text-red-600';
        title = 'Rejected';
    } else if (normalized === 'PAID') {
        icon = faCircleCheck;
        color = 'text-green-600';
        title = 'Reimbursement paid';
    } else if (normalized === 'CANCELLED') {
        icon = faCircleXmark;
        color = 'text-red-600';
        title = 'Reimbursement cancelled';
    }

    return <FontAwesomeIcon icon={icon} className={`text-base ${color}`} title={title} />;
};

const MobileExpenseCard = ({ expense, onView, onEdit, onDelete, onApprove, onReject, onReverse, canEdit = true, canDelete = true, canApprove = true, canReject = true, canReverse = false }) => {
    const [open, setOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        const onDocClick = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    const canModerate = expense.approval_status === 'PENDING';

    return (
        <Card className="p-3 overflow-visible">
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                            <Typography variant="small" className="font-semibold text-gray-800 truncate">
                                {expense.expense_no}
                            </Typography>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <span className={`inline-flex items-center rounded-full px-2 py-1 text-[11px] font-semibold ${expense.expense_type === 'VEHICLE' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                                    {expense.expense_type || 'N/A'}
                                </span>
                                <span className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold bg-gray-50 text-gray-700" title={expense.approval_status || 'N/A'}>
                                    <StatusIcon status={expense.approval_status} />
                                </span>
                                {expense.requires_reimbursement && (
                                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold bg-gray-50 text-gray-700" title={expense.reimbursement_status || 'N/A'}>
                                        <StatusIcon status={expense.reimbursement_status} type="reimbursement" />
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="text-right ml-3">
                            <Typography variant="small" className="font-semibold text-gray-800">
                                {formatCurrency(expense.amount)}
                            </Typography>
                            <Typography variant="small" className="text-gray-500">
                                {formatDate(expense.expense_date)}
                            </Typography>
                        </div>
                    </div>

                    <div className="mt-2 text-sm text-gray-600 truncate">
                        {expense.description || 'No description'}
                    </div>

                    <div className="mt-2 flex items-center gap-3 text-sm text-gray-500 flex-wrap">
                        {expense.plate_no && <span className="inline-block">🚘 {expense.plate_no}</span>}
                        {expense.owner_name && <span className="inline-block">👤 {expense.owner_name}</span>}
                        {expense.supplier_name && <span className="inline-block">🏷️ {expense.supplier_name}</span>}
                    </div>
                </div>

                <div ref={menuRef} className="relative flex-shrink-0">
                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-sm hover:bg-gray-50 hover:text-[#FF5733]"
                        title="More actions"
                        aria-label={`More actions for ${expense.expense_no}`}
                    >
                        <FontAwesomeIcon icon={faEllipsisVertical} />
                    </button>

                    {open && (
                        <div className="absolute right-0 top-full mt-2 w-44 rounded-none border border-gray-200 bg-white shadow-2xl z-[9999] overflow-hidden">
                            <button type="button" onClick={() => { setOpen(false); onView(expense); }} className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                                <FontAwesomeIcon icon={faEye} className="text-blue-600" /> View
                            </button>
                            {canApprove && canReject && canModerate && (
                                <>
                                    <button type="button" onClick={() => { setOpen(false); onApprove(expense.expense_id); }} className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                                        <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" /> Approve
                                    </button>
                                    <button type="button" onClick={() => { setOpen(false); onReject(expense.expense_id); }} className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                                        <FontAwesomeIcon icon={faTimesCircle} className="text-red-600" /> Reject
                                    </button>
                                </>
                            )}
                            {canEdit && (
                                <button type="button" onClick={() => { setOpen(false); onEdit(expense); }} className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                                    <FontAwesomeIcon icon={faEdit} className="text-amber-600" /> Edit
                                </button>
                            )}
                            {canDelete && !expense.is_reversed && (
                                <button type="button" onClick={() => { setOpen(false); onDelete(expense.expense_id); }} className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                                    <FontAwesomeIcon icon={faTrash} className="text-red-600" /> Delete
                                </button>
                            )}
                            {canReverse && expense.approval_status === 'APPROVED' && !expense.is_reversed && (
                                <button type="button" onClick={() => { setOpen(false); onReverse(expense.expense_id, expense.expense_no); }} className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                                    <FontAwesomeIcon icon={faUndo} className="text-orange-500" /> Reverse
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
};

export default MobileExpenseCard;
